import React, { createContext, useContext, useMemo } from 'react';

import { expandWildcard } from './permissions.config';
import { grantsInclude } from './effectivePermissions';

/**
 * Context for the current user's permission set.
 *
 * The set is populated at login from the API response (or built from the
 * user's role on the client side as a fallback). It contains a flat list
 * of CONCRETE permission keys — no wildcards, no role names.
 *
 * Components read from this context via the `usePermission` hook.
 */

export type PermissionsContextValue = {
    /** Set of permission keys the current user has, after wildcard expansion. */
    grants: Set<string>;
    /** True if the user is a superuser (bypasses all checks). */
    isSuperUser: boolean;
};

const DEFAULT_VALUE: PermissionsContextValue = {
    grants: new Set(),
    isSuperUser: false
};

export const PermissionsContext = createContext<PermissionsContextValue>(DEFAULT_VALUE);

/**
 * Provider — wrap your app root with this. The provider takes the user's
 * raw permission list (string keys, possibly containing wildcards) and
 * expands wildcards into a Set of concrete keys for fast O(1) lookups.
 */
type ProviderProps = {
    /** Raw permission keys from the role (may contain wildcards like 'service.*:view') */
    permissions: string[];
    isSuperUser?: boolean;
    children: React.ReactNode;
};

export const PermissionsProvider: React.FC<ProviderProps> = ({
    permissions,
    isSuperUser = false,
    children
}) => {
    const value = useMemo<PermissionsContextValue>(() => {
        const grants = new Set<string>();
        for (const p of permissions) {
            for (const key of expandWildcard(p)) {
                grants.add(key);
            }
        }
        return { grants, isSuperUser };
    }, [permissions, isSuperUser]);

    return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

/** Internal — used by the hooks. */
export function useGrants(): PermissionsContextValue {
    return useContext(PermissionsContext);
}

/**
 * Test if a permission key matches the user's grant set.
 * Supports wildcards like 'service.*:view' or '*:view' at check time.
 */
/**
 * Re-exported from the shared resolver so the browser and the authorize
 * middleware answer "does this set satisfy this key" identically.
 */
export const checkGrant = grantsInclude;
